/* eslint-disable @typescript-eslint/no-explicit-any */
import { LocalDate, LocalTime } from 'js-joda';
import { Redirect, Route, useHistory, useLocation, useRouteMatch } from 'react-router';
import { RouteComponentProps, Switch } from 'react-router-dom';
import { offeringsSelector } from 'selectors/calendarSelectors';
import { parse, stringify } from '@tock/query-string';
import BusinessProfileContent from 'components/business/landing-page/BusinessProfileContent'; // BASIC ROUTES
import LogoutRoute from 'routes/protected/LogoutRoute';
import ProtectedRoute from 'routes/ProtectedRoute';
import SearchModalRoute from 'routes/business/SearchModalRoute';

import {
  ACTION_BUSINESS_CLICK_MOBILE_RESERVATION_SEARCH,
  ACTION_SEARCH_BUSINESS_CALENDAR,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { consumer } from '@tock/coretypes';
import { getDateRange } from 'utils/offerings';
import { getNextRelease } from 'utils/businessUtils';
import { joinPath } from 'utils/pathUtils';
import { offeringSlugConsumerRoutePattern } from '@tock/shared/utils/offerings';
import { toNumber } from 'es-toolkit/compat';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useConsumerConversionAnalytics } from 'hooks/analytics/useConsumerConversionAnalytics';
import { useMountEffect } from '@tock/hooks';
import { useSelector } from 'react-redux';
import WaitlistRoute, { WaitlistLocationState } from 'routes/business/WaitlistRoute';
import WalkinWaitlistModalContent from 'components/business/walkin-waitlist/WalkinWaitlistModalContent';

const defaultTime = () => {
  return LocalTime.of(19);
};

interface MatchParams {
  widget: string;
}

interface OwnProps {
  setNavigation(isBusinessProfile: boolean): void;
}

const BusinessProfileRoute: React.FunctionComponent<OwnProps> = ({ setNavigation }) => {
  const {
    app,
    offerings,
    calendar: { attemptedOnce, calendar },
  } = useSelector(mapStateToProps);
  const { actions } = useActions();
  const history = useHistory();
  const match = useRouteMatch<MatchParams>();
  const location = useLocation();

  const amplitudeSendEvent = useAmplitudeSendEvent();
  const sendConversionAnalytics = useConsumerConversionAnalytics();

  useMountEffect(() => {
    actions.waitlist.restoreWalkinWaitlist();
    setNavigation(true);
  });

  const handleAddTicketTypeToWaitlist = (experienceId: number) => {
    if (experienceId) {
      history.push(joinPath(match.url, `/waitlist?experienceId=${experienceId}`));
    } else {
      history.push(joinPath(match.url, `/waitlist`));
    }
  };

  const handleSearch = (size: number, date: any, time: any) => {
    amplitudeSendEvent(ACTION_SEARCH_BUSINESS_CALENDAR, {
      partySize: size,
      date: date,
      time: time,
    });
    history.push({
      pathname: joinPath(match.url, `/search`),
      search: stringify({
        size,
        date,
        time,
      }),
    });
  };

  const handleMobileSearch = () => {
    const query = parse(location.search);

    const minDate = offerings && getDateRange(offerings).minDate;

    const size = query.size ? parseInt(query.size, 10) : 2;
    const date = query.date || minDate || LocalDate.now().toString();
    const time = query.time || defaultTime().toString();

    handleSearch(size, date, time);

    sendConversionAnalytics(ACTION_BUSINESS_CLICK_MOBILE_RESERVATION_SEARCH);
  };

  const handleCloseWalkinWaitlistModal = () => {
    history.replace(match.url);
  };

  const renderWidgetRedirect = () => {
    return <Redirect to={{ ...location, pathname: joinPath(match.url, `/search`) }} />;
  };

  if (!app?.config?.business) {
    return null;
  }
  const isLoading = !offerings && !attemptedOnce;
  const {
    config: { business },
  } = app;
  const nextRelease = getNextRelease(app.config);
  const widgetOnlyRoutes = [
    <Route key={1} exact path={match.path} render={renderWidgetRedirect} />,
    <Route key={2} path={joinPath(match.path, `/experiences`)} component={SearchModalRoute} />,
  ];

  const widgetRoutes = [
    <Route
      key={3}
      path={joinPath(match.path, `/${offeringSlugConsumerRoutePattern('offering')}`)}
      component={SearchModalRoute}
    />,
    <Route key={4} path={joinPath(match.path, `/private-dining`)} component={SearchModalRoute} />,
    <Route key={5} path={joinPath(match.path, `/search`)} component={SearchModalRoute} />,
    <Route key={6} path={joinPath(match.path, `/waitlist`)}>
      {(routeProps: RouteComponentProps<any, {}, WaitlistLocationState>) => (
        <WaitlistRoute
          experienceId={
            parse(routeProps.location.search).experienceId
              ? toNumber(parse(routeProps.location.search).experienceId)
              : undefined
          }
          {...routeProps}
        />
      )}
    </Route>,
    <Route
      key={7}
      path={joinPath(match.path, `/walkin-waitlist`)}
      render={(routeProps: RouteComponentProps<any>) =>
        business?.onlineWalkinWaitlistEnabled ? (
          <WalkinWaitlistModalContent
            {...routeProps}
            business={business}
            onClose={handleCloseWalkinWaitlistModal}
          />
        ) : (
          <Redirect to={`/${business?.domainName}`} />
        )
      }
    />,
  ];

  if (match.params.widget) {
    return (
      <Switch>
        {widgetOnlyRoutes}
        {widgetRoutes}
      </Switch>
    );
  }
  const ticketGroups = calendar ? calendar.ticketGroup : null;

  return (
    <>
      <Switch>
        {widgetRoutes}
        <ProtectedRoute app={app} path={joinPath(match.path, `/logout`)} component={LogoutRoute} />
      </Switch>
      <BusinessProfileContent
        business={business as consumer.BusinessConfig}
        isLoading={isLoading}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        nextRelease={nextRelease!}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        offerings={offerings!}
        onAddTicketTypeToWaitlist={handleAddTicketTypeToWaitlist}
        onMobileSearch={handleMobileSearch}
        onSearch={handleSearch}
        ticketGroups={ticketGroups as consumer.CalendarTicketGroup[]}
      />
    </>
  );
};

const mapStateToProps = (state: StoreType) => ({
  app: state.app,
  calendar: state.calendar,
  lock: state.lock,
  ui: state.ui,
  offerings: offeringsSelector(state),
});

export default BusinessProfileRoute;
